import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { enquiriesApi } from '../../services/smartBillApi';
import './Enquiries.css';

const emptyForm = {
  person_name: '',
  phone: '',
  work_purpose: '',
  remarks: '',
};

/**
 * Standalone enquiry registration — callback / WhatsApp when product or service is available.
 * Writes to enquiries with source=standalone_form (does not use Smart Bill).
 */
const EnquiryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill;
  const fromViewBill = Boolean(prefill?.bill_data?.linked_bill_id);

  const initialForm = useMemo(
    () => ({
      person_name: prefill?.person_name || '',
      phone: prefill?.phone || '',
      work_purpose: prefill?.work_purpose || '',
      remarks: prefill?.remarks || '',
    }),
    [prefill],
  );

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const person_name = form.person_name.trim();
    const phone = form.phone.replace(/\D/g, '').slice(0, 10);
    const work_purpose = form.work_purpose.trim();

    if (!person_name) {
      toast.error('Person name is required');
      return;
    }
    if (phone.length !== 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    if (!work_purpose) {
      toast.error('Product / service wanted is required');
      return;
    }

    try {
      setSaving(true);
      const response = await enquiriesApi.create({
        person_name,
        phone,
        work_purpose,
        remarks: form.remarks.trim() || null,
        status: 'interested',
        source: 'standalone_form',
        services: [],
        total_amount: 0,
        ...(prefill?.bill_data ? { bill_data: prefill.bill_data } : {}),
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to save enquiry');
      }

      toast.success('Enquiry registered. You can call / WhatsApp from Leads → Enquiries.');
      setForm(emptyForm);
      navigate('/bills/operator-calls?tab=enquiries&source=new', { replace: true });
    } catch (err) {
      console.error('Failed to register enquiry:', err);
      toast.error(err.message || 'Failed to register enquiry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="enquiries-page">
      <header className="enquiries-header">
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1>Enquiry Form</h1>
            <p>
              {fromViewBill
                ? 'Follow-up callback for this bill’s service (e.g. allotment update).'
                : 'Register a customer to call / WhatsApp when the product or service is available.'}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/bills/operator-calls?tab=enquiries&source=new')}
          >
            View Enquiries
          </button>
        </div>
      </header>

      <form className="enquiry-edit-form" onSubmit={handleSubmit} style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="form-group">
          <label htmlFor="person_name">Person name *</label>
          <input
            id="person_name"
            type="text"
            value={form.person_name}
            onChange={(e) => handleChange('person_name', e.target.value)}
            placeholder="Customer name"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone *</label>
          <input
            id="phone"
            type="tel"
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="10-digit mobile"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="work_purpose">Product / service wanted *</label>
          <input
            id="work_purpose"
            type="text"
            value={form.work_purpose}
            onChange={(e) => handleChange('work_purpose', e.target.value)}
            placeholder="What they asked for"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="remarks">Remarks</label>
          <textarea
            id="remarks"
            rows={3}
            value={form.remarks}
            onChange={(e) => handleChange('remarks', e.target.value)}
            placeholder="Optional notes (e.g. call for allotment update)"
          />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Register Enquiry'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={saving}
            onClick={() => setForm(emptyForm)}
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default EnquiryForm;
